import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { BiListCheck } from 'react-icons/bi';

// components
import { Dropdown } from 'components/Dropdown';
import { ConfirmSelectionModal } from 'components/modals/ConfirmSelectionModal';
// types
import { QuoteItem } from 'queries/approveOrder/types';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';

type Props = {
  quoteItems: Array<QuoteItem>;
  handleQuoteItemClick: (updatedQuoteItems: Array<QuoteItem>) => void;
  onSubmit: () => Promise<void>;
  isApproved: boolean;
  isPreview: boolean;
  mainColor?: string;
  quoteItemUpdated?: boolean;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  background: #ffffff;
  box-shadow: 0px 4px 16px rgba(29, 30, 36, 0.06);
  border-radius: 7px;
  box-sizing: border-box;
  width: 384px;
  margin: 0 auto 16px auto;
  @media screen and (max-width: 520px) {
    border-radius: 0px;
    width: 100%;
    padding: 24px 20px 20px;
    margin: 0;
  }
`;

type RowProps = {
  margin?: string;
};
const Row = styled.div<RowProps>`
  display: flex;
  box-sizing: border-box;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  margin: ${props => (props.margin ? props.margin : 0)};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
`;

const Heading = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const List = styled.ul`
  padding: 0px;
  list-style: none;
  margin: 0px;
`;

const ListItem = styled.li`
  width: 100%;
  background: #ffffff;
  border: 1px solid #d0d3d9;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 5px;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: #272a32;
  padding: 4px;
`;

const QuoteTitle = styled.span`
  margin: 0 16px 0 8px;
`;

const PriceText = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  text-align: right;
  color: ${({ theme }) => theme.colors.black[100]};
  margin-right: 0px;
  margin-left: auto;
`;

const TotalPriceText = styled(PriceText)`
  font-weight: bold;
  font-size: 18px;
  margin-left: 10px;
  margin-right: 24px;
`;
const Label = styled.div`
  display: flex;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: #9297a2;
`;

const BoldLabel = styled(Label)`
  font-weight: 600;
`;

const TotalPriceLabel = styled(Label)`
  font-weight: 700;
  font-size: 18px;
  margin-right: 0px;
  margin-left: auto;
`;

const ListWrapper = styled.div`
  margin-bottom: 24px;
`;

const TaxInfo = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.theme.colors.neutral[80]};
  text-align: right;
  width: 100%;
  margin-right: 24px;
`;

type StatusItems = {
  serviceId: number;
  title: string;
  chosen: boolean;
  option: string;
};

export const ServiceRequests = ({
  quoteItems = [],
  handleQuoteItemClick,
  onSubmit,
  isApproved = false,
  isPreview = false,
  mainColor,
  quoteItemUpdated = false,
}: Props) => {
  const [totalAmount, setTotalAmount] = React.useState<number | string>(0.0);
  const { t } = useTranslation();
  const [requiredQuoteItems, setRequiredQuoteItems] = React.useState<
    QuoteItem[]
  >([]);
  const [optionalQuoteItems, setOptionalQuoteItems] = React.useState<
    QuoteItem[]
  >([]);
  const [requiredItemsDropdownStatus, setRequiredItemsDropDownStatus] =
    React.useState<StatusItems[]>([]);
  const [optionalItemsDropdownStatus, setOptionalItemsDropDownStatus] =
    React.useState<StatusItems[]>([]);
  const [requiredItemsTotalAmount, setRequiredItemsTotalAmount] =
    React.useState<number | string>(0.0);
  const [optionalItemsTotalAmount, setOptionalItemsTotalAmount] =
    React.useState<number | string>(0.0);
  const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const [isDropDownDisabled, setIsDropDownDisabled] = React.useState(false);
  const [touchedItems, setTouchedItems] = React.useState<number[]>([]);
  React.useEffect(() => {
    if (quoteItems.length) {
      const requiredItemsDropdownArray: StatusItems[] = [];
      const optionalItemsDropdownArray: StatusItems[] = [];
      const touchedItemsArray: number[] = [];
      for (const item of quoteItems) {
        let option = '';
        if (item.isTouched) {
          touchedItemsArray.push(item.serviceId);
          option = item.approved ? 'Approve' : 'Deny';
        }

        if (item.required)
          requiredItemsDropdownArray.push({
            serviceId: item.serviceId,
            title: item.title,
            chosen: !!item.isTouched,
            option,
          });
        else
          optionalItemsDropdownArray.push({
            serviceId: item.serviceId,
            title: item.title,
            chosen: !!item.isTouched,
            option,
          });
      }
      setTouchedItems(touchedItemsArray);
      setRequiredItemsDropDownStatus(requiredItemsDropdownArray);
      setOptionalItemsDropDownStatus(optionalItemsDropdownArray);
    }
  }, []);

  React.useEffect(() => {
    calculateAndSetPricing(quoteItems);
  }, [quoteItems]);

  useEffect(() => {
    const allDropDownOptions = [
      ...requiredItemsDropdownStatus,
      ...optionalItemsDropdownStatus,
    ];
    const numberOfUnchosenOptions = allDropDownOptions.filter(
      (item: any) => item.chosen === false
    ).length;
    const areDropDownsFilled = numberOfUnchosenOptions === 0;

    setIsSubmitDisabled(!areDropDownsFilled);
  }, [requiredItemsDropdownStatus, optionalItemsDropdownStatus]);

  const updateDropDown = (option: string, quoteItem: QuoteItem) => {
    const dropDownArray = quoteItem.required
      ? requiredItemsDropdownStatus
      : (optionalItemsDropdownStatus as any);
    const setDropDownArray = quoteItem.required
      ? setRequiredItemsDropDownStatus
      : setOptionalItemsDropDownStatus;
    const newDropDownArray: StatusItems[] = dropDownArray.map(
      (item: StatusItems, index: number) => {
        if (item.serviceId === quoteItem.serviceId) {
          dropDownArray[index].chosen = true;
          dropDownArray[index].option = option;
        }

        return dropDownArray[index];
      }
    );
    setDropDownArray(newDropDownArray);
  };

  const calculateAndSetPricing = (quoteItems: QuoteItem[]) => {
    const requriedItemsArray: QuoteItem[] = [];
    const optionalItemsArray: QuoteItem[] = [];
    let requiredTotalAmount: number = 0.0;
    let optionalTotalAmount: number = 0.0;
    let disableSubmit = true;
    for (const item of quoteItems) {
      if (item.required === true) {
        requriedItemsArray.push(item);

        if (item.approved && item.price != null && !isNaN(item.price)) {
          requiredTotalAmount += parseFloat(item.price.toString());
          disableSubmit = false;
        }
      } else {
        optionalItemsArray.push(item);
        if (item.approved && item.price != null && !isNaN(item.price)) {
          optionalTotalAmount += parseFloat(item.price.toString());
        }
      }
    }

    setRequiredQuoteItems(requriedItemsArray);
    setOptionalQuoteItems(optionalItemsArray);
    setRequiredItemsTotalAmount(requiredTotalAmount.toFixed(2));
    setOptionalItemsTotalAmount(optionalTotalAmount.toFixed(2));
    setTotalAmount((requiredTotalAmount + optionalTotalAmount).toFixed(2));
    setIsSubmitDisabled(disableSubmit);
  };

  const approveOrDenyItem = (option: string, quoteItem: QuoteItem) => {
    switch (option.toUpperCase()) {
      case 'APPROVE':
        quoteItem.approved = true;
        quoteItem.isTouched = true;
        quoteItem.hasRequestedInfo = false;
        updateDropDown(option, quoteItem);
        break;
      case 'DENY':
        quoteItem.approved = false;
        quoteItem.isTouched = true;
        quoteItem.hasRequestedInfo = false;
        updateDropDown(option, quoteItem);
        break;
      case 'REQUEST INFO':
        quoteItem.hasRequestedInfo = true;
        quoteItem.isTouched = true;
        updateDropDown(option, quoteItem);
        break;
      default:
        break;
    }
    handleQuoteItemClick(quoteItems);
    calculateAndSetPricing(quoteItems);
  };

  const getListItems = (quoteItem: QuoteItem, index: number) => {
    const allDropDowns = [
      ...requiredItemsDropdownStatus,
      ...optionalItemsDropdownStatus,
    ];
    const option = allDropDowns.filter(
      item => item.serviceId === quoteItem.serviceId
    );
    let selectedOption = { chosen: false, option: '' };
    if (option.length) selectedOption = option[0];

    let initOption =
      isApproved || quoteItem.isTouched || isDropDownDisabled
        ? quoteItem.approved
          ? t('Approve')
          : t('Deny')
        : '';
    if (!!quoteItem.hasRequestedInfo) {
      initOption = t('Request Info');
    }
    return (
      <ListItem key={index}>
        <QuoteTitle>{quoteItem.title}</QuoteTitle>
        <PriceText
          style={{
            ...(((selectedOption.chosen && selectedOption.option === 'Deny') ||
              isApproved ||
              isDropDownDisabled) &&
              !quoteItem.approved &&
              !quoteItem.hasRequestedInfo && {
                textDecoration: 'line-through',
              }),
          }}
        >
          {`$${!!quoteItem.price ? parseFloat(quoteItem.price.toString()).toFixed(2) : '0.00'}`}
        </PriceText>
        <Dropdown
          options={[t('Approve'), t('Deny'), t('Request Info')]}
          onSelectOption={(e: any) => {
            approveOrDenyItem(e.target.innerText, quoteItem);
          }}
          disabled={
            isApproved ||
            isDropDownDisabled ||
            touchedItems.includes(quoteItem.serviceId)
          }
          mainColor={mainColor}
          initOption={initOption}
        />
      </ListItem>
    );
  };

  const submitHandler: any = async () => {
    setIsSubmitDisabled(true);
    setIsDropDownDisabled(true);
    await onSubmit().catch(ex => ex);
    setIsSubmitDisabled(false);
    // close the modal
    setIsConfirmModalOpen(false);
  };
  const openConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  return (
    <Container>
      <Row>
        <Column>
          {requiredQuoteItems.length > 0 && (
            <ListWrapper>
              <Heading>{t('Select Required Items')}</Heading>
              <Row margin='0 0 3px'>
                <BoldLabel>
                  {t('Items')}{' '}
                  <Label> &nbsp;{`${requiredQuoteItems.length}`},</Label>
                </BoldLabel>{' '}
                &nbsp;
                <BoldLabel>
                  {t('Total')}{' '}
                  <Label>&nbsp;{`$${requiredItemsTotalAmount}`}</Label>
                </BoldLabel>
              </Row>
              <List>
                {requiredQuoteItems.map(
                  (quoteItem: QuoteItem, index: number) => {
                    return getListItems(quoteItem, index);
                  }
                )}
              </List>
            </ListWrapper>
          )}
          {optionalQuoteItems.length > 0 && (
            <ListWrapper>
              <Heading>{t('Select Optional Items')}</Heading>
              <Row margin='0 0 3px'>
                <BoldLabel>
                  {t('Items')}{' '}
                  <Label> &nbsp;{`${optionalQuoteItems.length}`},</Label>
                </BoldLabel>{' '}
                &nbsp;
                <BoldLabel>
                  {t('Total')}{' '}
                  <Label>&nbsp;{`$${optionalItemsTotalAmount}`}</Label>
                </BoldLabel>
              </Row>
              <List>
                {optionalQuoteItems.map(
                  (quoteItem: QuoteItem, index: number) => {
                    return getListItems(quoteItem, index);
                  }
                )}
              </List>
            </ListWrapper>
          )}
        </Column>
      </Row>
      <Row>
        <TotalPriceLabel>{t('Total')}: </TotalPriceLabel>
        <TotalPriceText> {`$${totalAmount}`}</TotalPriceText>
      </Row>
      <Row margin='8px 0 0'>
        <TaxInfo>*{t('plus taxes and applicable fees')}</TaxInfo>
      </Row>
      <Row margin='24px 0 0'>
        <Button
          disabled={
            isSubmitDisabled || isApproved || isPreview || !quoteItemUpdated
          }
          style={{
            backgroundColor: mainColor ? mainColor : '#FF8B22',
            color: 'white',
            borderColor: 'transparent',
          }}
          text={t('Send My Selection')}
          icon={<BiListCheck />}
          onClick={openConfirmModal}
        />
      </Row>

      {isConfirmModalOpen && (
        <ConfirmSelectionModal
          quoteItems={quoteItems}
          handleSubmit={submitHandler}
          handleClose={() => setIsConfirmModalOpen(false)}
        />
      )}
    </Container>
  );
};
